import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { logout } from "../features/userSlice";
import { useNavigate } from "react-router-dom";
import { setIsBulkCallsEnabled } from "../features/bulkCallsSlice";
import {
  usersPostRequest,
  updateRequest,
  patchRequest,
  dateRangeRequest,
  paginatedRequest,
  fetchUserRecord,
  deleteRequest,
  authorizedGetRequest,
  multipartPostRequest,
} from "./api/httpClient";
import { createTicketsApi } from "./api/ticketsApi";
import { createAgentConfigApi } from "./api/agentConfigApi";
import { createToolDefinitionsApi } from "./api/toolDefinitionsApi";
import { createOrgPageAccessApi, createHasPageAccess } from "./api/orgPageAccessApi";
import { createClientAlertConfigApi } from "./api/clientAlertConfigApi";

const StateManagement = createContext(null);
const useContextStateManagement = () => useContext(StateManagement);

const SIDEBAR_LINK_STORAGE_KEY = "cm_lastSidebarLink";
const ACTIVE_TAB_STORAGE_KEY = "cm_lastActiveTab";
const SELECTED_PRACTICE_STORAGE_KEY = "cm_selectedPracticeId";

const getInitialSidebarLink = () => {
  if (typeof window === "undefined") {
    return "careConsole";
  }

  const currentPath = window.location.pathname;

  if (currentPath === "/support") {
    return "support";
  }

  if (currentPath === "/homepage") {
    return "homepage";
  }

  if (currentPath === "/tickets") {
    return "tickets";
  }

  if (currentPath === "/dialer") {
    return "dialer";
  }

  if (currentPath === "/prompt-hub" || currentPath.startsWith("/prompt-hub/")) {
    return "onBoardingForm";
  }

  if (currentPath === "/agent-config") {
    return "agentConfig";
  }

  if (currentPath === "/api-integration") {
    return "apiIntegration";
  }

  if (currentPath === "/tools") {
    return "toolDefinitions";
  }

  if (currentPath === "/add-users") {
    return "addUsers";
  }

  if (currentPath === "/admin-center") {
    return "manageOrg";
  }

  // Remaining sidebar sections (Call Settings, Bulk Upload, etc.) fall
  // back to the last section the user was actually on, so a refresh
  // doesn't reset them to the default console view.
  return sessionStorage.getItem(SIDEBAR_LINK_STORAGE_KEY) || "careConsole";
};

const getInitialActiveTab = () => {
  if (typeof window === "undefined") {
    return "inBoundCalls";
  }

  return sessionStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || "inBoundCalls";
};

const ContextStateManagement = ({ children }) => {
  const [memberListName, setMemberListName] = useState(
    "Inbound Calls"
  );
  const [activeTab, setActiveTab] = useState(getInitialActiveTab);
  const [isRightsLoad, setIsRightsLoad] = useState(false);
  const [onBoardingRightsId, setOnBoardingRightsId] = useState(1);
  const [rightsEnabled, setRightsEnabled] = useState(false);
  const [PracticeId, setPracticeId] = useState("");
  // Bumped whenever the user saves a new PreferredTimezone/TimezoneSyncMode in Settings (see
  // Home.jsx's handleSaveTimezone) - components that fetch their own practiceTimezone
  // (Dashboard.jsx, InboundOverview.jsx, OutboundOverview.jsx, usePracticeTimezone) add this to
  // their fetch effect's dependency array so a save refetches and picks up the new zone
  // immediately, instead of requiring a manual page reload.
  const [timezonePreferenceVersion, setTimezonePreferenceVersion] = useState(0);
  const bumpTimezonePreferenceVersion = () => setTimezonePreferenceVersion((v) => v + 1);
  const [practiceContactNumber, setPracticeContactNumber] = useState("");
  const [userName, setUserName] = useState();
  const [userEmail, setUserEmail] = useState("");
  const [userProfession, setUserProfession] = useState();
  const [dateUrl, setDateUrl] = useState();
  const [sideBarLinkActive, setSideBarLinkActive] = useState(getInitialSidebarLink);
  const [currentUrl, setCurrentUrl] = useState("");
  const [dashboardUrl, setDashboardUrl] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [activeOnBoardingTem, setActiveOnBoardingTem] = useState();
  const [callSettingsData, setCallSettingsData] = useState([]);
  const [doNotCallList, setDoNotCallList] = useState("");
  const [callAttemptsPerDay, setCallAttemptsPerDay] = useState("");
  const [timeOfCall, setTimeOfCall] = useState("");
  const [startTimeOfCall, setStartTimeOfCall] = useState("08AM");
  const [endTimeOfCall, setEndTimeOfCall] = useState("08AM");
  const [concurrentCalls, setConcurrentCalls] = useState(10);
  const [batchTimeBetweenCalls, setBatchTimeBetweenCalls] = useState(10);
  const [followUpCalls, setFollowUpCalls] = useState("");
  const [dayOfCall, setDayOfCall] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [memberCallSettingsData, setMemberCallSettingsData] = useState([]);
  const [memberDoNotCallList, setMemberDoNotCallList] = useState("");
  const [memberCallAttemptsPerDay, setMemberCallAttemptsPerDay] = useState("");
  const [memberTimeOfCall, setMemberTimeOfCall] = useState("");
  const [memberFollowUpCalls, setMemberFollowUpCalls] = useState("");
  const [memberDayOfCall, setMemberDayOfCall] = useState([]);
  const [memberPatientName, setMemberPatientName] = useState("");
  const [memberProviderName, setMemberProviderName] = useState("");
  const [providerId, setProviderId] = useState(0);
  const [providerData, setProviderData] = useState([]);
  const [accessRights, setAccessRights] = useState([]);
  const [token, setToken] = useState("");
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [tokenExpiryTime, setTokenExpiryTime] = useState();
  const [activeUploadLead, setActiveUploadLead] = useState();
  const [userType, setUserType] = useState("client");
  const [practicesList, setPracticesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [practiceName, setPracticeName] = useState("");
  const [ownPracticeId, setOwnPracticeId] = useState("");
  const [ownPracticeName, setOwnPracticeName] = useState("");
  const [userAdmin, setUserAdmin] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [liscenseType, setLiscenseType] = useState("");
  const [practiceUsername, setPracticeUsername] = useState("");
  const [outBoundContact, setOutBoundContact] = useState("");
  const [inboundContact, setInboundContact] = useState("");
  const [practiceCreateDate, setPracticeCreateDate] = useState("");
  const [credentialsSaved, setCredentialsSaved] = useState(false);
  const [orgAllowedPages, setOrgAllowedPages] = useState([]);
  const [orgPageAccessConfigured, setOrgPageAccessConfigured] = useState(false);
  const timeoutIdRef = useRef(null);
  const phoneTabSetterRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // useSelector for getting logged in user token
  const user = useSelector((state) => state.user.user);

  const {
    createTicket,
    getMyTickets,
    getAllTickets,
    getTicketById,
    updateTicketStatus,
    updateTicketType,
    addTicketComment,
    sendTicketEmail,
    getTicketAssigneeSuggestions,
    assignTicket,
    uploadTicketAttachment,
  } = createTicketsApi({ PracticeId, user });

  const { getOrgPageAccess, saveOrgPageAccess } = createOrgPageAccessApi({ user });

  const {
    getAgentConfigDraft,
    saveAgentConfigDraft,
    submitAgentConfigDraft,
    getSubmittedAgentConfig,
    generateAgentConfigSuggestions,
    getAgentConfigSuggestions,
    markAgentConfigSuggestionsViewed,
    publishAgentConfigDraft,
    getAgentConfigHistory,
    getAgentConfigCurrentLive,
    rollbackAgentConfigVersion,
    startAgentConfigDemoCall,
    endAgentConfigDemoCall,
    getAgentConfigTestPhoneNumber,
    saveAgentConfigTestPhoneNumber,
    startAgentConfigPhoneDemoCall,
  } = createAgentConfigApi({ user });

  const {
    getToolDefinitionsDraft,
    saveToolDefinitionsDraft,
    submitToolDefinitionsDraft,
    getSubmittedToolDefinitions,
    publishToolDefinitionsDraft,
    getToolDefinitionsHistory,
    rollbackToolDefinitionsVersion,
  } = createToolDefinitionsApi({ user });

  const { getClientAlertConfig, saveClientAlertConfig } = createClientAlertConfigApi({ user });

  const hasPageAccess = createHasPageAccess({
    userType,
    orgAllowedPages,
    orgPageAccessConfigured,
  });

  const logoutFromServer = async () => {
    if (!user?.token) return;
    return usersPostRequest(user.token, "/api/logout", {});
  };

  // Logging out must drop the previously-selected org, otherwise the next
  // login on this browser (a different user, or the same admin) silently
  // reuses whichever practice was last viewed instead of that user's own.
  const clearSelectedOrg = () => {
    setPracticeId("");
    localStorage.removeItem(SELECTED_PRACTICE_STORAGE_KEY);
  };

  const handleTokenExpires = (tokenExpTime) => {
    // Clear any timer from a previous login before scheduling a new one —
    // fetchData() re-running (e.g. on a fresh login in the same session)
    // used to stack up an extra orphaned setTimeout each time, since the
    // previous timer's id was never actually stored anywhere to clear it.
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    const expiryTime = tokenExpTime * 1000 - Date.now();
    if (expiryTime > 0) {
      timeoutIdRef.current = setTimeout(() => {
        timeoutIdRef.current = null;
        setIsTokenExpired(true);
      }, expiryTime);
    }
  };

  // The setTimeout above isn't reliable on its own for a delay this long:
  // browsers throttle or fully suspend timers in a backgrounded tab, and a
  // laptop sleep/wake cycle can make it fire very late — which is why
  // reopening a stale tab previously just showed nothing until an unrelated
  // API call happened to trip the same isTokenExpired check (e.g. on
  // reload). Checking the token's real expiry as soon as the tab becomes
  // visible again catches it immediately instead of waiting on the timer.
  useEffect(() => {
    const checkExpiryOnFocus = () => {
      if (document.visibilityState !== "visible") return;
      if (!token || !tokenExpiryTime || isTokenExpired) return;
      if (tokenExpiryTime * 1000 - Date.now() <= 0) {
        setIsTokenExpired(true);
      }
    };
    document.addEventListener("visibilitychange", checkExpiryOnFocus);
    window.addEventListener("focus", checkExpiryOnFocus);
    return () => {
      document.removeEventListener("visibilitychange", checkExpiryOnFocus);
      window.removeEventListener("focus", checkExpiryOnFocus);
    };
  }, [token, tokenExpiryTime, isTokenExpired]);

  const registerPhoneTabSetter = (setterFn) => {
    phoneTabSetterRef.current = setterFn;
  };

  const closePhoneTab = () => {
    if (phoneTabSetterRef.current) {
      phoneTabSetterRef.current(false);
    }
  };

  const fetchData = async () => {
    setToken(user.token);
    const userData = await fetchUserRecord(user.token, "/api/currentUser");
    if (userData == "Token is expired, login again") {
      setPracticeId("");
      setPracticeContactNumber("");
      setPracticeCreateDate("");
      setIsTokenExpired(true);
    } else if (userData && userData.data && userData.message) {
      let practiceId = userData.data.PracticeId;
      let practiceContact = userData.data.PracticeContactNumber || userData.message.PracticeContact;
      let outBoundContactNo = userData.data.OutBoundContactNumber || userData.message.OutBoundContact;
      let inboundContactNo = userData.data.InBoundContactNumber || userData.message.InboundContact;
      let practiceName = userData.data.PracticeName || userData.message.PracticeName;
      let practiceCreatedAt = userData.data.PracticeCreateDateTime || "";
      let userName = userData.data.UserName;
      let userEmail = userData.data.UserEmail || userData.data.Email || "";
      let userProfession = userData.data.UserProfession;
      let userOfType = userData.data.User_type;
      let user_ID = userData.data.UserId;
      let licence = userData.data.LicenseType;
      let isCredentialsSaved = userData.credentialsSaved;
      setUserName(userName);
      setUserEmail(userEmail);
      setUserAdmin(userProfession);
      setUserProfession(userProfession);
      setPracticeName(practiceName);
      setOwnPracticeId(practiceId);
      setOwnPracticeName(practiceName);
      setTokenExpiryTime(userData.message.exp);
      handleTokenExpires(userData.message.exp);
      setUserType(userOfType);
      setLiscenseType(licence);
      setCredentialsSaved(isCredentialsSaved);

      if (userOfType === "admin") {
        fetchPracticesList(userOfType);
        return;
      }

      setPracticeUsername(userName);
      setPracticeContactNumber(practiceContact);
      setOutBoundContact(outBoundContactNo);
      setInboundContact(inboundContactNo);
      setPracticeCreateDate(practiceCreatedAt);
      setCurrentUserId(user_ID);
      setPracticeId(practiceId);
      setUserType(userOfType);
      setActiveOnBoardingTem(`/api/PAuthQOnBoardingTemplate/${PracticeId}`);
    }
  };

  useEffect(() => {
    // debugger
    if (user && user.token) {
      fetchData();
    }
    // userType is intentionally excluded - fetchData() itself calls setUserType(),
    // so including it here re-triggers this effect a second time on every login,
    // firing /api/currentUser and fetchPracticesList() twice in a row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Remember the current sidebar section / careConsole tab so a refresh
  // lands back where the user was instead of resetting to the defaults.
  useEffect(() => {
    sessionStorage.setItem(SIDEBAR_LINK_STORAGE_KEY, sideBarLinkActive);
  }, [sideBarLinkActive]);

  useEffect(() => {
    sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    let ignore = false;

    const loadOrgAccess = async () => {
      if (!user?.token || !PracticeId || userType === "admin") {
        if (!ignore) {
          setOrgAllowedPages([]);
          setOrgPageAccessConfigured(false);
        }
        return;
      }

      try {
        const access = await getOrgPageAccess(PracticeId);
        if (ignore) {
          return;
        }
        setOrgAllowedPages(access.allowedPages);
        setOrgPageAccessConfigured(access.configured);
      } catch (error) {
        if (!ignore) {
          setOrgAllowedPages([]);
          setOrgPageAccessConfigured(false);
        }
      }
    };

    loadOrgAccess();

    return () => {
      ignore = true;
    };
  }, [PracticeId, user?.token, userType]);

  async function fetchUsersList(practiceId, token) {
    if (practiceId) {
      try {
        const userData = await fetchUserRecord(
          token,
          `/api/getUsersList?practiceId=${practiceId}`
        );
        if (!userData) {
          console.error("userData is undefined/null");
          setUsersList([]);
          return;
        }

        if (userData === "Token is expired, login again") {
          setIsTokenExpired(true);
          setUsersList([]);
        } else if (userData?.error === "No rows found") {
          setUsersList([]);
        } else if (Array.isArray(userData)) {
          setUsersList(userData);
        } else {
          console.error("Unexpected userData format:", userData);
          setUsersList([]);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsersList([]);
      }
    }
  }

  async function fetchPracticesList(userType) {
    // debugger;
    if (userType === "admin") {
      const userData = await fetchUserRecord(
        user.token,
        `/api/getPracticesList`
      );
      if (userData === "Token is expired, login again") {
        setIsTokenExpired(true);
      } else if (userData === "No rows found") {
        setPracticesList([]);
      } else if (Array.isArray(userData) && userData.length > 0) {
        // Keep whatever organization the user last selected (if it still
        // exists in the list) instead of always defaulting back to the
        // first one every time this list is (re)fetched, e.g. on refresh.
        const savedPracticeId = localStorage.getItem(SELECTED_PRACTICE_STORAGE_KEY);
        const savedPractice = savedPracticeId
          ? userData.find((practice) => String(practice.Id) === savedPracticeId)
          : null;
        const selectedPractice = savedPractice || userData[0];

        let practiceId = selectedPractice.Id;
        let practiceContact = selectedPractice.PracticeContactNumber;
        let outBoundContactNo = selectedPractice.OutBoundContactNumber;
        let inboundContactNo = selectedPractice.InBoundContactNumber;
        let practiceName = selectedPractice.PracticeName;
        let practiceCreatedAt = selectedPractice.PracticeCreateDateTime || "";

        console.log('[ContextApi] fetchPracticesList - Practice data:', {
          practiceId,
          practiceContact,
          outBoundContactNo,
          inboundContactNo,
          practiceName
        });

        //let userName = userData[0].UserName;
        // let userProfession = userData[0].UserProfession;
        // let user_Id = userData[0].UserId;
        // let licence = userData[0].LicenseType;

        //setCurrentUserId(user_Id);
        setPracticesList(userData);
        // setLiscenseType(licence);
        setPracticeName(practiceName);
        // setUserName(userName);
        setPracticeUsername(practiceName);
        // setUserProfession(userProfession);
        setPracticeContactNumber(practiceContact);
        setOutBoundContact(outBoundContactNo);
        setInboundContact(inboundContactNo);
        setPracticeCreateDate(practiceCreatedAt);
        console.log('[ContextApi] Setting inboundContact to:', inboundContactNo);
        setPracticeId(practiceId);
        setCredentialsSaved(Boolean(selectedPractice.credentialsSaved));
        setActiveOnBoardingTem(`/api/PAuthQOnBoardingTemplate/${practiceId}`);
      }
    }
  }

  const fetchApiHandle = useCallback(async (callMember, activeTab) => {
    if (callMember === "generalCallSettings" || !callMember) {
      const userData = await fetchUserRecord(
        user.token,
        `/api/GeneralCallSettings/${PracticeId}`
      );
      if (userData == "Token is expired, login again") {
        setIsTokenExpired(true);
      } else if (
        userData ==
        "General call settings are not saved yet, please add settings and saved"
      ) {
        setCallSettingsData([]);
        setDoNotCallList("");
        setCallAttemptsPerDay("");
        setStartTimeOfCall("08AM");
        setEndTimeOfCall("08AM");
        setConcurrentCalls(10);
        setBatchTimeBetweenCalls(10);
        setFollowUpCalls("");
        setDayOfCall([]);
        setSelectedTimezone("");

        if (activeTab == "leadGTab") {
        } else {
          alert(userData);
        }
      } else if (Array.isArray(userData) && userData.length > 0) {
        setCallSettingsData(userData);
        setDoNotCallList(userData[0].DoNotCallList);
        setCallAttemptsPerDay(userData[0].NumberOfCallAttemptPerDays);
        setStartTimeOfCall(userData[0].StartTimeOfCall);
        setEndTimeOfCall(userData[0].EndTimeOfCall);
        setConcurrentCalls(userData[0].ConcurrentCalls);
        setBatchTimeBetweenCalls(userData[0].BatchCallsTime);
        setFollowUpCalls(userData[0].FollowUpCallRelation);
        setDayOfCall(userData[0].DayOfCall);
        setSelectedTimezone(userData[0].SelectedTimezone || "");
      }
    }

    if (callMember === "CallSettingsMemberListWise" || !callMember) {
      const userDataMemberCallSettings = await fetchUserRecord(
        user.token,
        `/api/CallSettingsMemberListWise/${PracticeId}`
      );
      if (userDataMemberCallSettings == "Token is expired, login again") {
        setIsTokenExpired(true);
      } else if (
        userDataMemberCallSettings ==
        "Specific member call settings are not saved yet, please add settings and saved"
      ) {
        setMemberCallSettingsData([]);
        // alert(userDataMemberCallSettings)
      } else if (
        Array.isArray(userDataMemberCallSettings) &&
        userDataMemberCallSettings.length > 0
      ) {
        setMemberCallSettingsData(userDataMemberCallSettings);
        setMemberPatientName(userDataMemberCallSettings[0].PatientName);
        setMemberProviderName(userDataMemberCallSettings[0].ProviderDetails);
        setMemberDoNotCallList(userDataMemberCallSettings[0].DoNotCallList);
        setMemberCallAttemptsPerDay(
          userDataMemberCallSettings[0].NumberOfCallAttemptPerDays
        );
        setMemberTimeOfCall(userDataMemberCallSettings[0].TimeOfCall);
        setMemberFollowUpCalls(
          userDataMemberCallSettings[0].FollowUpCallRelation
        );
        setMemberDayOfCall(userDataMemberCallSettings[0].DayOfCall);
      }
    }
    // fetchUserRecord is a pure function of its (token, apiUrl) args - it doesn't close
    // over any component state, so a stale reference to it behaves identically to a
    // fresh one and it's safe to omit here. Omitting it (rather than including it and
    // having it change every render) is what keeps fetchApiHandle's own identity stable
    // between renders - every consumer that lists fetchApiHandle in a useEffect
    // dependency array (Sidebar.jsx, LeadGGrid.jsx) was re-firing that effect on every
    // render because this function had a new reference each time, causing an unbounded
    // request loop against GeneralCallSettings/CallSettingsMemberListWise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, PracticeId]);

  useEffect(() => {
    if (PracticeId) {
      setDoNotCallList("");
      setCallAttemptsPerDay("");
      setTimeOfCall("");
      setFollowUpCalls("");
      setDayOfCall([]);
      setSelectedTimezone("");
      setMemberPatientName("");
      setMemberProviderName("");
      setMemberDoNotCallList("");
      setMemberCallAttemptsPerDay("");
      setMemberTimeOfCall("");
      setMemberFollowUpCalls("");
      setMemberDayOfCall([]);
    }
  }, [PracticeId]);

  useEffect(() => {
    if (userType !== "admin" || !PracticeId || !Array.isArray(practicesList) || practicesList.length === 0) {
      return;
    }

    const selectedPractice = practicesList.find(
      (practice) => Number(practice.Id) === Number(PracticeId),
    );

    if (!selectedPractice) {
      return;
    }

    setPracticeCreateDate(selectedPractice.PracticeCreateDateTime || "");
    setPracticeName(selectedPractice.PracticeName || "");
    setPracticeContactNumber(selectedPractice.PracticeContactNumber || "");
    setOutBoundContact(selectedPractice.OutBoundContactNumber || "");
    setInboundContact(selectedPractice.InBoundContactNumber || "");
  }, [PracticeId, practicesList, userType]);

  // useEffect(() => {

  //   if (!PracticeId) return;

  //   if (activeTab === "pAuthQTab") {
  //     setActiveOnBoardingTem(`/api/PAuthQOnBoardingTemplate/${PracticeId}`);
  //     setActiveUploadLead(`/api/PriorAuthQLead/${PracticeId}`);
  //   } else if (activeTab === "pAuthSTab") {
  //     setActiveOnBoardingTem(`/api/PAuthStatusOnboarding/${PracticeId}`);
  //     setActiveUploadLead(`/api/PAuthStatusUploadLead`);
  //   } else if (activeTab === "benefitVTab") {
  //     setActiveOnBoardingTem(`/api/BenefitsVerificationOnboarding/${PracticeId}`);
  //     setActiveUploadLead(`/api/BenefitsVerificationUploadLead`);
  //   } else if (activeTab === "careRTab") {
  //     setActiveOnBoardingTem(`/api/CareRemindersOnboarding/${PracticeId}`);
  //     setActiveUploadLead(`/api/CareRemindersUploadLead`);
  //   } else if (activeTab === "visitRTab") {
  //     setActiveOnBoardingTem(`/api/VisitRemindersOnboarding/${PracticeId}`);
  //     setActiveUploadLead(`/api/VisitRemindersUploadLead`);
  //   } else if (activeTab === "awareNessCTab") {
  //     setActiveOnBoardingTem(`/api/AwarenessCampaignOnBoarding/${PracticeId}`);
  //     setActiveUploadLead(`/api/AwarenessCampaignUploadLead`);
  //   } else if (activeTab === "leadGTab") {
  //     setActiveOnBoardingTem(`/api/LeadGenerationOnboarding/${PracticeId}`);
  //     setActiveUploadLead(`/api/getLeadGenerationUploadLead`);
  //   } else if (activeTab === "uatTab") {
  //     setDateUrl(`/api/SearchDateUatTest`);
  //   }
  // }, [PracticeId, activeTab])

  // This is the ONE handler for token expiry - every other file that used to watch
  // isTokenExpired had its own copy-pasted version of this exact effect (alert + cleanup +
  // navigate), which meant a single expiry fired one alert() per still-mounted component
  // simultaneously (the user had to click OK multiple times) plus redundant logout()/navigate()
  // calls. Those duplicates are removed - this is now the only place that reacts to
  // isTokenExpired becoming true.
  //
  // No more alert()/manual OK click - redirects immediately, and carries the current route so
  // Login.jsx's existing location.state.from -> sessionStorage("postLoginRedirect") mechanism
  // (already used for "hit a protected route while logged out") returns the user to the exact
  // page (including query params, e.g. a selected date range) they were on once they log back in.
  useEffect(() => {
    if (isTokenExpired === true) {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      setToken("");
      setIsTokenExpired(false);
      clearSelectedOrg();
      dispatch(logout());
      navigate("/login", { state: { from: returnTo } });
    }
  }, [isTokenExpired]);

  return (
    <StateManagement.Provider
      value={{
        memberListName,
        setMemberListName,
        activeTab,
        setActiveTab,
        tokenExpiryTime,
        deleteRequest,
        practicesList,
        setPracticesList,
        usersList,
        setUsersList,
        setCurrentUserId,
        currentUserId,
        PracticeId,
        timezonePreferenceVersion,
        bumpTimezonePreferenceVersion,
        ownPracticeId,
        ownPracticeName,
        practiceContactNumber,
        sideBarLinkActive,
        setSideBarLinkActive,
        setPracticeId,
        clearSelectedOrg,
        setUserName,
        setUserProfession,
        userAdmin,
        setUserAdmin,
        setDateUrl,
        currentUrl,
        setCurrentUrl,
        userName,
        userEmail,
        setUserEmail,
        userProfession,
        dashboardUrl,
        setPracticeContactNumber,
        setUserType,
        userType,
        liscenseType,
        setLiscenseType,
        setDashboardUrl,
        activeOnBoardingTem,
        setActiveOnBoardingTem,
        fetchApiHandle,
        callSettingsData,
        setCallSettingsData,
        fetchUsersList,
        fetchPracticesList,
        doNotCallList,
        setDoNotCallList,
        callAttemptsPerDay,
        setCallAttemptsPerDay,
        timeOfCall,
        setTimeOfCall,
        followUpCalls,
        setFollowUpCalls,
        dayOfCall,
        setDayOfCall,
        memberCallSettingsData,
        setMemberCallSettingsData,
        memberDoNotCallList,
        setMemberDoNotCallList,
        setPracticeName,
        practiceName,
        setOwnPracticeName,
        memberCallAttemptsPerDay,
        setMemberCallAttemptsPerDay,
        memberTimeOfCall,
        setMemberTimeOfCall,
        memberFollowUpCalls,
        setMemberFollowUpCalls,
        memberDayOfCall,
        setMemberDayOfCall,
        memberPatientName,
        setMemberPatientName,
        memberProviderName,
        setMemberProviderName,
        practiceUsername,
        setPracticeUsername,
        providerId,
        setProviderId,
        providerData,
        setProviderData,
        accessRights,
        fetchUserRecord,
        authorizedGetRequest,
        token,
        setToken,
        setTokenExpiryTime,
        outBoundContact,
        setOutBoundContact,
        practiceCreateDate,
        setPracticeCreateDate,
        usersPostRequest,
        patchRequest,
        multipartPostRequest,
        createTicket,
        getMyTickets,
        getAllTickets,
        getTicketById,
        updateTicketStatus,
        updateTicketType,
        addTicketComment,
        sendTicketEmail,
        getTicketAssigneeSuggestions,
        assignTicket,
        uploadTicketAttachment,
        logoutFromServer,
        tokenError,
        setTokenError,
        updateRequest,
        dateRangeRequest,
        paginatedRequest,
        activeUploadLead,
        setActiveUploadLead,
        startTimeOfCall,
        setStartTimeOfCall,
        endTimeOfCall,
        setEndTimeOfCall,
        concurrentCalls,
        setConcurrentCalls,
        batchTimeBetweenCalls,
        setBatchTimeBetweenCalls,
        timeoutIdRef,
        onBoardingRightsId,
        setOnBoardingRightsId,
        rightsEnabled,
        setRightsEnabled,
        isRightsLoad,
        setIsRightsLoad,
        inboundContact,
        setInboundContact,
        selectedTimezone,
        setSelectedTimezone,
        credentialsSaved,
        setCredentialsSaved,
        orgAllowedPages,
        setOrgAllowedPages,
        orgPageAccessConfigured,
        setOrgPageAccessConfigured,
        getOrgPageAccess,
        saveOrgPageAccess,
        getClientAlertConfig,
        saveClientAlertConfig,
        getAgentConfigDraft,
        saveAgentConfigDraft,
        submitAgentConfigDraft,
        getSubmittedAgentConfig,
        generateAgentConfigSuggestions,
        getAgentConfigSuggestions,
        markAgentConfigSuggestionsViewed,
        publishAgentConfigDraft,
        getAgentConfigHistory,
        getAgentConfigCurrentLive,
        rollbackAgentConfigVersion,
        startAgentConfigDemoCall,
        endAgentConfigDemoCall,
        getAgentConfigTestPhoneNumber,
        saveAgentConfigTestPhoneNumber,
        startAgentConfigPhoneDemoCall,
        getToolDefinitionsDraft,
        saveToolDefinitionsDraft,
        submitToolDefinitionsDraft,
        getSubmittedToolDefinitions,
        publishToolDefinitionsDraft,
        getToolDefinitionsHistory,
        rollbackToolDefinitionsVersion,
        hasPageAccess,
        registerPhoneTabSetter,
        closePhoneTab,
      }}
    >
      {children}
    </StateManagement.Provider>
  );
};

export { useContextStateManagement, ContextStateManagement };
